import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError } from '../../api/client.ts';
import { Can } from '../access/Can.tsx';
import { emptyAudience } from '../audience/AudienceBuilder.tsx';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/**
 * Settings → Suppression lists (BB-49): saved audiences that receive nothing from any campaign or Canvas while active,
 * with their current size. Only admins create and activate them.
 */
export function SuppressionListsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/suppression-lists' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/suppression-lists' });
  const navigate = useNavigate();
  const lists = useQuery({ queryKey: ['suppression-lists', environmentId], queryFn: () => api.suppressionLists(environmentId) });
  const sizes = useQueries({ queries: (lists.data?.items ?? []).map((l) => ({ queryKey: ['audience-estimate', environmentId, l.definition], queryFn: () => api.estimateAudience(environmentId, l.definition), retry: false })) });
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api.createSuppressionList(environmentId, { name: name.trim(), definition: emptyAudience() }),
    onSuccess: (list) => void navigate({ to: '/env/$environmentId/settings/suppression-lists/$listId', params: { environmentId, listId: list.id } }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };
  return (
    <section>
      <h1>Suppression lists</h1>
      <p className="hint">Users in an active list receive no campaign or Canvas message, except messages with one of the list's exception tags or transactional ones.</p>
      {lists.isPending ? (
        <p aria-busy="true">Loading…</p>
      ) : lists.isError ? (
        <ErrorState title="Suppression lists could not be loaded" message={errorText(lists.error)} onRetry={() => void lists.refetch()} />
      ) : lists.data.items.length === 0 ? (
        <EmptyState title="No suppression lists" description="Nobody is excluded globally." />
      ) : (
        <table className="table" aria-label="Suppression lists">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Status</th>
              <th scope="col">Users now</th>
              <th scope="col">Exception tags</th>
            </tr>
          </thead>
          <tbody>
            {lists.data.items.map((l, i) => (
              <tr key={l.id}>
                <td>
                  <Link to="/env/$environmentId/settings/suppression-lists/$listId" params={{ environmentId, listId: l.id }}>
                    {l.name}
                  </Link>
                  {l.description && <div className="hint">{l.description}</div>}
                </td>
                <td>{l.status === 'active' ? <span className="tag">Active</span> : <span className="tag tag--neutral">Inactive</span>}</td>
                <td>{sizes[i]?.data ? `${sizes[i]!.data!.mode === 'estimate' ? '≈' : ''}${formatCount(sizes[i]!.data!.count)}` : '—'}</td>
                <td>{l.exceptionTagIds.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Can permission="env.manage">
        <form className="stack-form" aria-label="Create suppression list" onSubmit={submit}>
          <label>
            List name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <Button type="submit" disabled={!name.trim() || create.isPending}>
            Create list
          </Button>
          {create.isError && <span role="alert">{errorText(create.error)}</span>}
        </form>
      </Can>
    </section>
  );
}
